import { PANEL_LOGIN_ROLE_IDS } from '../constants/croles';

const TOKEN_KEY = 'gise_panel_access_token';
const USER_KEY = 'gise_panel_user';

const getApiUrl = () => {
  const base = process.env.REACT_APP_API_URL ?? 'http://localhost:3001';
  return base.replace(/\/$/, '');
};

const ALLOWED_ROLES = PANEL_LOGIN_ROLE_IDS;

const parseJson = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const apiAuthProvider = {
  login: async ({ username, password }) => {
    const response = await fetch(`${getApiUrl()}/auth/panel/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: username, password }),
    });

    const data = await parseJson(response);

    if (!response.ok) {
      const message =
        data?.message ??
        (Array.isArray(data?.message) ? data.message.join(', ') : null) ??
        'Login error, invalid credentials';
      throw new Error(
        typeof message === 'string' ? message : 'Login error, invalid credentials',
      );
    }

    const { accessToken, user } = data;
    if (!accessToken || !user?.crole) {
      throw new Error('Login error, invalid permissions');
    }

    if (!ALLOWED_ROLES.includes(user.crole)) {
      throw new Error('Login error, invalid permissions');
    }

    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    return user;
  },

  logout: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    return Promise.resolve();
  },

  checkAuth: async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      return Promise.reject();
    }

    const response = await fetch(`${getApiUrl()}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      return Promise.reject();
    }

    const permissions = await parseJson(response);
    if (permissions?.crole) {
      localStorage.setItem(USER_KEY, JSON.stringify(permissions));
    }
    return Promise.resolve();
  },

  checkError: (error) => {
    const status = error?.status ?? error?.response?.status;
    if (status === 401 || status === 403) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      return Promise.reject();
    }
    return Promise.resolve();
  },

  getPermissions: async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      return Promise.reject();
    }

    const cached = localStorage.getItem(USER_KEY);
    if (cached) {
      try {
        const user = JSON.parse(cached);
        if (user.crole) {
          return {
            id: user.id,
            email: user.email,
            crole: user.crole,
            rid: user.rid,
          };
        }
      } catch {
        /* ignore */
      }
    }

    const response = await fetch(`${getApiUrl()}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      return Promise.reject();
    }

    const permissions = await parseJson(response);
    localStorage.setItem(USER_KEY, JSON.stringify(permissions));
    return permissions;
  },

  getIdentity: async () => {
    const permissions = await apiAuthProvider.getPermissions();
    return {
      id: permissions.id,
      fullName: permissions.fullName ?? permissions.email,
    };
  },
};

export const getPanelAccessToken = () => localStorage.getItem(TOKEN_KEY);

export default apiAuthProvider;
